import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { STORAGE_KEYS } from '@/utils/constants';
import { locationInfoApi } from '@/features/location/api/location-info-api';
import type { Operator } from '@/features/location/types/location-info.types';

export type SupportedLocale = 'en' | 'en-US' | 'da' | 'es' | 'fr' | 'de';

interface LocaleState {
  // State
  currentLocale: SupportedLocale;
  allOperators: Operator[]; // Cache of all operators from map_suggestions
  isOperatorsCacheInitialized: boolean;

  // Actions
  setLocale: (locale: SupportedLocale) => void;
  initialize: () => void;
  initializeOperatorsCache: () => Promise<void>;
}

export const useLocaleStore = create<LocaleState>()(
  devtools(
    persist(
      immer((set, get) => ({
        // Initial state
        currentLocale: 'en',
        allOperators: [],
        isOperatorsCacheInitialized: false,

        // Set locale
        setLocale: (locale: SupportedLocale) => {
          set({ currentLocale: locale });
          localStorage.setItem(STORAGE_KEYS.LANGUAGE, locale);
        },

        // Initialize locale
        // Priority: URL param (handled in useUrlParams) > Saved locale > Browser language
        initialize: () => {
          const savedLocale = localStorage.getItem(
            STORAGE_KEYS.LANGUAGE
          ) as SupportedLocale | null;

          const supportedLocales: SupportedLocale[] = [
            'en',
            'en-US',
            'da',
            'es',
            'fr',
            'de',
          ];

          // If we have a saved locale, use it
          if (savedLocale && supportedLocales.includes(savedLocale)) {
            set({ currentLocale: savedLocale });
            return;
          }

          // Otherwise, detect from browser
          // Try to detect the best matching locale from browser settings
          let detectedLocale: SupportedLocale = 'en'; // default fallback

          // Check navigator.languages (array of user's preferred languages)
          const browserLanguages = navigator.languages || [navigator.language];

          for (const browserLang of browserLanguages) {
            const langCode = browserLang.toLowerCase();

            // Try exact match first
            if (langCode === 'en-us') {
              detectedLocale = 'en-US';
              break;
            } else if (langCode === 'da' || langCode === 'da-dk') {
              detectedLocale = 'da';
              break;
            } else if (langCode.startsWith('es')) {
              detectedLocale = 'es';
              break;
            } else if (langCode.startsWith('fr')) {
              detectedLocale = 'fr';
              break;
            } else if (langCode.startsWith('de')) {
              detectedLocale = 'de';
              break;
            } else if (langCode.startsWith('en')) {
              // Prefer regular 'en' over 'en-US' for generic English
              if (
                langCode === 'en' ||
                langCode === 'en-gb' ||
                langCode === 'en-au'
              ) {
                detectedLocale = 'en';
                break;
              }
            }
          }

          set({ currentLocale: detectedLocale });
        },

        // Initialize operators cache
        // Fetches all operators from map_suggestions endpoint on app start
        initializeOperatorsCache: async () => {
          const { isOperatorsCacheInitialized, allOperators } = get();

          // Prevent multiple initializations if already initialized AND has operators
          if (isOperatorsCacheInitialized && allOperators.length > 0) {
            console.log(
              `[LocaleStore] Operators cache already initialized with ${allOperators.length} operators`
            );
            return;
          }

          try {
            console.log('[LocaleStore] Fetching all operators...');
            const operators = await locationInfoApi.getAllOperators();
            console.log(
              `[LocaleStore] Successfully cached ${operators.length} operators`
            );

            set({
              allOperators: operators,
              isOperatorsCacheInitialized: true,
            });
          } catch (error) {
            console.error(
              '[LocaleStore] Failed to initialize operators cache:',
              error
            );
            // Still mark as initialized to prevent retry loops
            set({
              allOperators: [],
              isOperatorsCacheInitialized: true,
            });
          }
        },
      })),
      {
        name: 'monta-locale',
        partialize: (state) => ({
          currentLocale: state.currentLocale,
          allOperators: state.allOperators,
          isOperatorsCacheInitialized: state.isOperatorsCacheInitialized,
        }),
      }
    ),
    { name: 'LocaleStore' }
  )
);
